<template>
  <div class="reports-page">
    <hs-empty-state
      v-if="!selectedStoreId && !isLoadingStores && stores.length === 0"
      icon="alert-circle"
      title="No stores available"
      description="You need access to at least one store to manage backups and reports."
    />

    <hs-empty-state
      v-else-if="!selectedStoreId && stores.length > 0"
      icon="map-pin"
      title="Select a store"
      description="Choose a store from the header to generate reports."
    />

    <div v-else class="reports-grid">
      <main class="reports-main">
        <section class="reports-card report-studio">
          <div class="report-studio__head">
            <div>
              <p class="reports-kicker">Report Studio</p>
              <h2>Dynamic reports</h2>
              <p>
                Preview first, then generate a file only when you choose a download format.
                New report types appear automatically from the server catalog.
              </p>
            </div>
            <div class="report-studio__actions">
              <hs-button
                type="button"
                variant="secondary"
                icon="layout"
                @click="openCustomReportDesigner"
              >
                New custom report
              </hs-button>
              <hs-button
                type="button"
                variant="success"
                :icon="isReportGenerationInFlight ? 'loader' : 'play-circle'"
                :loading="isSubmittingReport"
                :disabled="!canSubmitReportRequest || isSubmittingReport"
                @click="handleGenerateReport"
              >
                {{
                  isSubmittingReport
                    ? "Starting..."
                    : isReportGenerationInFlight
                      ? "Preparing..."
                      : generateButtonLabel
                }}
              </hs-button>
            </div>
          </div>

          <div class="report-studio__fields">
            <hs-select
              v-model="reportRequest.type"
              label="Report type"
              :options="reportTypeOptions"
            />
            <hs-select
              v-model="reportRequest.format"
              label="Format"
              :options="reportFormatOptions"
            />
          </div>

          <p class="report-studio__description">
            {{
              currentReportConfig?.description ||
              "Dynamic reports load from the server catalog for the selected store."
            }}
          </p>

          <hs-badge v-if="isLoadingReportCatalog" tone="info" icon="loader">
            Refreshing reports
          </hs-badge>
          <hs-badge v-else-if="!reportTypeOptions.length" tone="warning" icon="alert-triangle">
            No dynamic reports available
          </hs-badge>

          <div v-if="currentReportConfig?.requiresDateRange" class="report-studio__dates">
            <hs-date-input
              v-model="reportRequest.startDate"
              label="Start date"
              :max="reportRequest.endDate"
            />
            <hs-date-input
              v-model="reportRequest.endDate"
              label="End date"
              :min="reportRequest.startDate"
            />
          </div>
        </section>

        <section class="reports-card report-library">
          <div class="report-library__head">
            <div>
              <p class="reports-kicker">Report library</p>
              <h2>Saved reports and public templates</h2>
              <p>Open work from the designer or manage the public copies shared by this store.</p>
            </div>
            <hs-button
              variant="ghost"
              size="sm"
              icon="refresh-cw"
              :loading="isLoadingReportLibrary"
              @click="fetchReportLibrary"
            >
              Refresh
            </hs-button>
          </div>

          <div class="report-library__group">
            <div class="report-library__group-head">
              <h3>Starter templates</h3>
              <hs-badge tone="neutral">{{ systemTemplates.length }}</hs-badge>
            </div>
            <div v-if="systemTemplates.length" class="report-library__rows">
              <article
                v-for="template in systemTemplates"
                :key="template.key"
                class="report-library__row"
              >
                <div class="report-library__identity">
                  <hs-icon name="file-plus" :size="18" />
                  <div>
                    <strong>{{ template.name }}</strong>
                    <span>
                      {{ template.description }} · {{ template.sheet_count }} sheets ·
                      {{ template.table_count }} tables
                    </span>
                  </div>
                </div>
                <hs-badge tone="info">Built in</hs-badge>
                <hs-button
                  variant="secondary"
                  size="sm"
                  icon="copy"
                  :loading="cloningTemplateKey === template.key"
                  :disabled="Boolean(cloningTemplateKey)"
                  @click="cloneSystemTemplate(template)"
                >
                  Use
                </hs-button>
              </article>
            </div>
            <p v-else class="report-library__empty">
              No starter templates are available for your current permissions.
            </p>
          </div>

          <div class="report-library__group">
            <div class="report-library__group-head">
              <h3>Designer reports</h3>
              <hs-badge tone="neutral">{{ savedReports.length }}</hs-badge>
            </div>
            <div v-if="savedReports.length" class="report-library__rows">
              <article
                v-for="report in savedReports"
                :key="report.id"
                class="report-library__row"
              >
                <div class="report-library__identity">
                  <hs-icon name="layout" :size="18" />
                  <div>
                    <strong>{{ report.name }}</strong>
                    <span>
                      Updated {{ formatLibraryTime(report.updated_at) }} · revision {{ report.revision }}
                    </span>
                  </div>
                </div>
                <hs-badge :tone="report.status === 'PUBLISHED' ? 'success' : 'warning'">
                  {{ report.status === "PUBLISHED" ? "Published" : "Draft" }}
                </hs-badge>
                <div class="report-library__actions">
                  <hs-button
                    variant="secondary"
                    size="sm"
                    icon="edit-3"
                    @click="openSavedReport(report)"
                  >
                    Edit
                  </hs-button>
                  <hs-button
                    variant="ghost"
                    size="sm"
                    icon="trash-2"
                    @click="confirmLibraryDelete('report', report)"
                  >
                    Delete
                  </hs-button>
                </div>
              </article>
            </div>
            <p v-else class="report-library__empty">
              No designer reports yet. Create one to save a reusable workbook layout.
            </p>
          </div>

          <div class="report-library__group">
            <div class="report-library__group-head">
              <h3>Public templates</h3>
              <hs-badge tone="neutral">{{ publicTemplates.length }}</hs-badge>
            </div>
            <div v-if="publicTemplates.length" class="report-library__rows">
              <article
                v-for="template in publicTemplates"
                :key="template.uuid"
                class="report-library__row"
              >
                <div class="report-library__identity">
                  <hs-icon name="share-2" :size="18" />
                  <div>
                    <strong>{{ template.name }}</strong>
                    <span>{{ template.clone_count || 0 }} clones · public UUID</span>
                  </div>
                </div>
                <hs-badge tone="info">Public</hs-badge>
                <div class="report-library__actions">
                  <hs-button
                    variant="secondary"
                    size="sm"
                    icon="external-link"
                    @click="openPublicTemplate(template)"
                  >
                    Open
                  </hs-button>
                  <hs-button
                    variant="ghost"
                    size="sm"
                    icon="copy"
                    title="Copy public link"
                    @click="copyPublicTemplateLink(template)"
                  />
                  <hs-button
                    variant="ghost"
                    size="sm"
                    icon="edit-3"
                    title="Rename template"
                    @click="editPublicTemplate(template)"
                  />
                  <hs-button
                    variant="ghost"
                    size="sm"
                    icon="trash-2"
                    title="Unpublish template"
                    @click="confirmLibraryDelete('template', template)"
                  />
                </div>
              </article>
            </div>
            <p v-else class="report-library__empty">
              No public templates. Publish a designer report, then share it from the designer.
            </p>
          </div>

          <div class="report-library__group">
            <div class="report-library__group-head">
              <h3>Email delivery</h3>
              <div class="report-library__actions">
                <hs-badge tone="neutral">{{ customReportSubscriptions.length }}</hs-badge>
                <hs-button
                  variant="secondary"
                  size="sm"
                  icon="mail"
                  :disabled="!publishedReports.length"
                  @click="openSubscriptionDialog"
                >
                  Subscribe
                </hs-button>
              </div>
            </div>
            <div v-if="customReportSubscriptions.length" class="report-library__rows">
              <article
                v-for="subscription in customReportSubscriptions"
                :key="subscription.uuid"
                class="report-library__row"
              >
                <div class="report-library__identity">
                  <hs-icon name="mail" :size="18" />
                  <div>
                    <strong>{{ subscription.report_name }}</strong>
                    <span>
                      {{ formatSubscriptionFrequency(subscription.frequency) }} ·
                      {{ subscription.output_format }} · latest published revision
                    </span>
                  </div>
                </div>
                <hs-button
                  variant="ghost"
                  size="sm"
                  icon="trash-2"
                  @click="deleteCustomSubscription(subscription)"
                >
                  Unsubscribe
                </hs-button>
              </article>
            </div>
            <p v-else class="report-library__empty">
              Subscribe to a published report for recurring store-local delivery.
            </p>
          </div>
        </section>

        <section v-if="isReportGenerationInFlight" class="reports-status">
          <hs-icon name="loader" tone="success" soft spin box-size="3rem" :size="22" />
          <div>
            <strong>{{ generationStatusTitle }}</strong>
            <p>{{ generationStatusMessage }}</p>
          </div>
        </section>

        <StoreReportPreviewPanel
          :task="activeReportTask"
          :report-config="activeReportConfig"
          @download-file="downloadReportFile(activeReportTask, $event)"
        />
      </main>

      <aside class="reports-sidebar">
        <section class="reports-card reports-recent">
          <div class="reports-recent__head">
            <div>
              <p class="reports-kicker">Recent Previews</p>
              <p>Reopen a preview or trigger a download without leaving the current report.</p>
            </div>
            <div class="reports-recent__actions">
              <hs-badge tone="neutral">{{ reportTasksForSelectedStore.length }}</hs-badge>
              <hs-button
                v-if="reportTasksForSelectedStore.length"
                variant="ghost"
                size="sm"
                icon="trash-2"
                @click="clearStoreExports"
              >
                Clear
              </hs-button>
            </div>
          </div>

          <div v-if="reportTasksForSelectedStore.length" class="reports-task-list">
            <article
              v-for="task in reportTasksForSelectedStore"
              :key="task.id"
              class="reports-task"
              :class="{ 'is-selected': isPreviewTaskSelected(task) }"
            >
              <div class="reports-task__head">
                <strong>{{ getBackgroundTaskDisplayName(task) }}</strong>
                <hs-badge :tone="taskStatusTone(task)" size="sm">
                  {{ task.status || "PENDING" }}
                </hs-badge>
              </div>
              <p v-if="task.data?.startDate && task.data?.endDate">
                Period: {{ formatDateRange(task.data.startDate, task.data.endDate) }}
              </p>
              <p>Generated: {{ formatGenerationTime(task.startedAt) }}</p>

              <div class="reports-task__buttons">
                <hs-button
                  variant="secondary"
                  size="sm"
                  :icon="isPreviewTaskSelected(task) ? 'eye' : 'file-text'"
                  @click="selectPreviewTask(task.id)"
                >
                  {{ isPreviewTaskSelected(task) ? "Previewing" : "Preview" }}
                </hs-button>
                <hs-button
                  v-for="file in downloadOptionsForTask(task)"
                  :key="`${task.id}-${file.format || file.filename}`"
                  variant="success"
                  size="sm"
                  icon="download"
                  @click="downloadReportFile(task, file)"
                >
                  {{ file.format || "Download" }}
                </hs-button>
              </div>

              <div v-if="isTaskRunning(task)" class="reports-task__message">
                <hs-icon name="loader" spin :size="13" />
                <span>Preparing preview...</span>
              </div>
              <div v-else-if="isTaskFailed(task)" class="reports-task__message reports-task__message--danger">
                <hs-icon name="alert-circle" :size="13" />
                <span>
                  {{
                    task.data?.error ||
                    task.data?.result?.error ||
                    task.data?.result?.message ||
                    "Report generation failed"
                  }}
                </span>
              </div>

              <hs-button
                class="reports-task__remove"
                variant="ghost"
                size="sm"
                icon="x"
                @click="removeExportTask(task.id)"
              >
                Remove
              </hs-button>
            </article>
          </div>

          <hs-empty-state
            v-else
            icon="file-text"
            title="No previews yet"
            description="Run a report to populate this sidebar."
          />
        </section>
      </aside>
    </div>

    <hs-modal
      v-model="showTemplateEditDialog"
      title="Edit public template"
      description="Update the public listing. The published workbook structure remains unchanged."
      size="sm"
    >
      <div class="report-library__form">
        <hs-input
          v-model="templateForm.name"
          label="Template name"
          maxlength="128"
          required
        />
        <hs-textarea
          v-model="templateForm.description"
          label="Description"
          maxlength="512"
          :rows="4"
        />
      </div>
      <template #footer>
        <hs-button variant="secondary" @click="showTemplateEditDialog = false">
          Cancel
        </hs-button>
        <hs-button
          icon="save"
          :loading="isSavingTemplate"
          :disabled="!templateForm.name.trim()"
          @click="savePublicTemplate"
        >
          Save changes
        </hs-button>
      </template>
    </hs-modal>

    <hs-confirm-dialog
      v-model="showLibraryDeleteDialog"
      tone="danger"
      :title="libraryDeleteTitle"
      :message="libraryDeleteMessage"
      :confirm-text="pendingLibraryDelete?.type === 'template' ? 'Unpublish' : 'Delete report'"
      :loading="isDeletingLibraryItem"
      @confirm="deleteLibraryItem"
    />

    <hs-modal
      v-model="showSubscriptionDialog"
      title="Subscribe to a custom report"
      description="The latest published revision is delivered for the previous completed store-local period. Each table's saved delivery-row policy determines what is included."
      size="sm"
    >
      <div class="report-library__form">
        <hs-select
          v-model="subscriptionForm.definitionId"
          label="Published report"
          :options="publishedReportOptions"
        />
        <hs-select
          v-model="subscriptionForm.frequency"
          label="Frequency"
          :options="subscriptionFrequencyOptions"
        />
        <hs-select
          v-model="subscriptionForm.outputFormat"
          label="Attachments"
          :options="subscriptionFormatOptions"
        />
      </div>
      <template #footer>
        <hs-button variant="secondary" @click="showSubscriptionDialog = false">
          Cancel
        </hs-button>
        <hs-button
          icon="mail"
          :loading="isSavingSubscription"
          :disabled="!subscriptionForm.definitionId"
          @click="createCustomSubscription"
        >
          Subscribe
        </hs-button>
      </template>
    </hs-modal>
  </div>
</template>

<script>
import StoreReportPreviewPanel from "@/components/reports/StoreReportPreviewPanel.vue";
import http from "@/helper/http";
import api from "@/helper/api";
import { toast } from "vue3-toastify";
import { mapGetters } from "vuex";
import { useBackgroundTasks } from "@/composables/useBackgroundTasks";
import {
  formatErrorMessage,
  convertGMTToLocalTime,
  formatDate,
} from "@/helper/utils";
import {
  HsBadge,
  HsButton,
  HsConfirmDialog,
  HsDateInput,
  HsEmptyState,
  HsIcon,
  HsInput,
  HsModal,
  HsSelect,
  HsTextarea,
} from "@/components/ui";

export default {
  name: "StoreReportsView",
  components: {
    StoreReportPreviewPanel,
    HsBadge,
    HsButton,
    HsConfirmDialog,
    HsDateInput,
    HsEmptyState,
    HsIcon,
    HsInput,
    HsModal,
    HsSelect,
    HsTextarea,
  },
  setup() {
    const backgroundTasks = useBackgroundTasks();
    return {
      backgroundTasksList: backgroundTasks.tasks,
      addBackgroundTask: backgroundTasks.addTask,
      getBackgroundTaskDisplayName: backgroundTasks.getTaskDisplayName,
      removeBackgroundTask: backgroundTasks.removeTask,
      clearBackgroundTasksForStore: backgroundTasks.clearTasksForStore,
      isTaskRunning: backgroundTasks.isTaskRunning,
    };
  },
  data() {
    // Get today's date in local timezone for date filters.
    // Date inputs interpret date strings as UTC, so we convert local time to "fake UTC" ISO date
    const getLocalISODate = () => {
      const now = new Date();
      const offset = now.getTimezoneOffset();
      const local = new Date(now.getTime() - offset * 60 * 1000);
      return local.toISOString().split("T")[0];
    };
    const today = getLocalISODate();
    return {
      remoteReportTypeConfig: {},
      reportFormatLabels: {
        XLSX: "Excel (.xlsx)",
        PDF: "PDF",
        CSV: "CSV",
        BOTH: "PDF + Excel",
      },
      reportRequest: {
        type: "",
        format: "XLSX",
        startDate: today,
        endDate: today,
      },
      isSubmittingReport: false,
      isLoadingReportCatalog: false,
      selectedPreviewTaskId: null,
      systemTemplates: [],
      cloningTemplateKey: "",
      savedReports: [],
      publicTemplates: [],
      publishedReports: [],
      customReportSubscriptions: [],
      isLoadingReportLibrary: false,
      showTemplateEditDialog: false,
      editingTemplateUuid: "",
      templateForm: { name: "", description: "" },
      isSavingTemplate: false,
      showLibraryDeleteDialog: false,
      pendingLibraryDelete: null,
      isDeletingLibraryItem: false,
      showSubscriptionDialog: false,
      isSavingSubscription: false,
      subscriptionForm: {
        definitionId: "",
        frequency: "DAILY",
        outputFormat: "XLSX",
      },
    };
  },
  computed: {
    ...mapGetters(["getUserStores", "isLoadingStores"]),
    stores() {
      return this.getUserStores || [];
    },
    selectedStoreId() {
      const storeStateValue = this.normalizeStoreId(
        this.$store.state.dashboard?.selectedStore,
      );
      return (
        storeStateValue || this.normalizeStoreId(this.$route.params.storeId)
      );
    },
    reportTypeConfig() {
      return this.remoteReportTypeConfig;
    },
    reportTypeOptions() {
      return Object.keys(this.reportTypeConfig)
        .map((key) => ({
          value: key,
          ...this.reportTypeConfig[key],
        }))
        .sort(
          (left, right) => (left.sortOrder || 999) - (right.sortOrder || 999),
        );
    },
    currentReportConfig() {
      return this.reportTypeConfig[this.reportRequest.type] || null;
    },
    generateButtonLabel() {
      if (this.currentReportConfig?.previewEnabled === false) {
        return "Prepare report";
      }
      return "Preview";
    },
    availableReportFormatsForType() {
      const config = this.currentReportConfig;
      if (!config) return [];
      return config.formats;
    },
    reportFormatOptions() {
      return this.availableReportFormatsForType.map((formatKey) => ({
        value: formatKey,
        label: this.getReportFormatLabel(formatKey),
      }));
    },
    reportTasksForSelectedStore() {
      const allTasks = this.backgroundTasksList || [];
      return allTasks.filter(
        (task) =>
          task.type === "store_report" &&
          String(task.storeId) === String(this.selectedStoreId),
      );
    },
    activeReportTask() {
      const tasks = this.reportTasksForSelectedStore;
      if (!tasks.length) {
        return null;
      }

      if (this.selectedPreviewTaskId) {
        const selectedTask = tasks.find(
          (task) => String(task.id) === String(this.selectedPreviewTaskId),
        );
        if (selectedTask) {
          return selectedTask;
        }
      }

      return (
        tasks.find(
          (task) =>
            task.data?.result?.preview ||
            task.data?.result?.files?.length ||
            this.isTaskRunning(task),
        ) || tasks[0]
      );
    },
    activeReportConfig() {
      const reportType =
        this.activeReportTask?.data?.reportType || this.reportRequest.type;
      return (
        this.reportTypeConfig[reportType] || this.currentReportConfig || null
      );
    },
    isReportGenerationInFlight() {
      return Boolean(
        this.isSubmittingReport ||
          (this.activeReportTask && this.isTaskRunning(this.activeReportTask)),
      );
    },
    generationStatusTitle() {
      if (this.activeReportConfig?.previewEnabled === false) {
        return "Preparing report";
      }
      return "Preparing preview";
    },
    generationStatusMessage() {
      const reportLabel =
        this.activeReportTask?.data?.reportLabel ||
        this.activeReportConfig?.label ||
        this.currentReportConfig?.label ||
        "report";

      if (this.isSubmittingReport && !this.activeReportTask) {
        return `Starting ${reportLabel.toLowerCase()}. This view will update as soon as the request is accepted.`;
      }

      if (this.activeReportConfig?.previewEnabled === false) {
        return `The latest ${reportLabel.toLowerCase()} is being prepared. Download options will appear here when it is ready.`;
      }

      return `The latest ${reportLabel.toLowerCase()} preview is being prepared. You can keep working here or reopen earlier runs from the sidebar.`;
    },
    canSubmitReportRequest() {
      const config = this.currentReportConfig;
      if (
        !config ||
        !this.selectedStoreId ||
        this.isLoadingReportCatalog ||
        !this.reportTypeOptions.length
      ) {
        return false;
      }
      if (config.requiresDateRange) {
        return Boolean(
          this.reportRequest.startDate && this.reportRequest.endDate,
        );
      }
      return true;
    },
    libraryDeleteTitle() {
      return this.pendingLibraryDelete?.type === "template"
        ? "Unpublish public template?"
        : "Delete designer report?";
    },
    libraryDeleteMessage() {
      const itemName = this.pendingLibraryDelete?.item?.name || "this item";
      return this.pendingLibraryDelete?.type === "template"
        ? `${itemName} will stop being available at its public UUID. You can share the source report again later.`
        : `${itemName} will be removed from this store's report library. Any public template links created from it will also be unpublished.`;
    },
    publishedReportOptions() {
      return this.publishedReports.map((report) => ({
        value: report.id,
        label: report.name,
      }));
    },
    subscriptionFrequencyOptions() {
      return [
        { value: "DAILY", label: "Daily" },
        { value: "WEEKLY", label: "Weekly" },
        { value: "MONTHLY", label: "Monthly" },
      ];
    },
    subscriptionFormatOptions() {
      return [
        { value: "XLSX", label: "Excel" },
        { value: "PDF", label: "PDF" },
        { value: "BOTH", label: "Excel + PDF" },
      ];
    },
  },
  watch: {
    "reportRequest.type"(newType) {
      this.syncReportFormatWithType(newType);
    },
    async selectedStoreId(newStoreId, oldStoreId) {
      if (String(newStoreId) === String(oldStoreId)) {
        return;
      }
      this.resetReportForm();
      await Promise.all([
        this.fetchReportCatalog(),
        this.fetchReportLibrary(),
      ]);
    },
    "$route.params.storeId": {
      immediate: true,
      handler() {
        this.syncSelectedStoreFromRoute();
      },
    },
    "$route.query.reportType": {
      immediate: true,
      handler() {
        this.applyRouteReportTypePreset();
      },
    },
  },
  methods: {
    openCustomReportDesigner() {
      this.$router.push({
        name: "report_designer",
        params: { storeId: this.selectedStoreId },
      });
    },
    async openSavedReport(report) {
      try {
        if (report.status === "PUBLISHED") {
          await http.post(
            api.store_report_definition_draft(
              this.selectedStoreId,
              report.id,
            ),
          );
        }
        await this.$router.push({
          name: "report_designer",
          params: { storeId: this.selectedStoreId },
          query: { reportId: report.id },
        });
      } catch (error) {
        toast.error("The report could not be opened for editing.");
      }
    },
    async cloneSystemTemplate(template) {
      if (!template?.key || this.cloningTemplateKey) return;
      this.cloningTemplateKey = template.key;
      try {
        const response = await http.post(
          api.clone_store_report_template(this.selectedStoreId, template.key),
        );
        const definition = response?.data?.data?.definition;
        if (!definition?.id) throw new Error("Missing cloned report");
        await this.$router.push({
          name: "report_designer",
          params: { storeId: this.selectedStoreId },
          query: { reportId: definition.id },
        });
      } catch (error) {
        toast.error("The starter template could not be created.");
      } finally {
        this.cloningTemplateKey = "";
      }
    },
    openPublicTemplate(template) {
      this.$router.push({
        name: "public_report_template",
        params: { templateUuid: template.uuid },
      });
    },
    publicTemplateUrl(template) {
      const route = this.$router.resolve({
        name: "public_report_template",
        params: { templateUuid: template.uuid },
      });
      return `${window.location.origin}${route.href}`;
    },
    async copyPublicTemplateLink(template) {
      try {
        await navigator.clipboard.writeText(this.publicTemplateUrl(template));
        toast.success("Template link copied");
      } catch (error) {
        toast.error("The public link could not be copied.");
      }
    },
    editPublicTemplate(template) {
      this.editingTemplateUuid = template.uuid;
      this.templateForm = {
        name: template.name || "",
        description: template.description || "",
      };
      this.showTemplateEditDialog = true;
    },
    async savePublicTemplate() {
      const name = this.templateForm.name.trim();
      if (!name || !this.editingTemplateUuid || this.isSavingTemplate) return;
      this.isSavingTemplate = true;
      try {
        await http.patch(
          api.store_public_report_template(
            this.selectedStoreId,
            this.editingTemplateUuid,
          ),
          {
            name,
            description: this.templateForm.description.trim(),
          },
        );
        this.showTemplateEditDialog = false;
        await this.fetchReportLibrary();
        toast.success("Public template updated");
      } catch (error) {
        toast.error("The public template could not be updated.");
      } finally {
        this.isSavingTemplate = false;
      }
    },
    confirmLibraryDelete(type, item) {
      this.pendingLibraryDelete = { type, item };
      this.showLibraryDeleteDialog = true;
    },
    async deleteLibraryItem() {
      if (!this.pendingLibraryDelete || this.isDeletingLibraryItem) return;
      this.isDeletingLibraryItem = true;
      const { type, item } = this.pendingLibraryDelete;
      try {
        const endpoint =
          type === "template"
            ? api.store_public_report_template(this.selectedStoreId, item.uuid)
            : api.store_report_definition(this.selectedStoreId, item.id);
        await http.delete(endpoint);
        this.showLibraryDeleteDialog = false;
        this.pendingLibraryDelete = null;
        await this.fetchReportLibrary();
        toast.success(type === "template" ? "Template unpublished" : "Report deleted");
      } catch (error) {
        toast.error(
          type === "template"
            ? "The template could not be unpublished."
            : "The report could not be deleted.",
        );
      } finally {
        this.isDeletingLibraryItem = false;
      }
    },
    async fetchReportLibrary() {
      if (!this.selectedStoreId) {
        this.systemTemplates = [];
        this.savedReports = [];
        this.publicTemplates = [];
        return;
      }
      this.isLoadingReportLibrary = true;
      try {
        const [
          systemTemplatesResponse,
          definitionsResponse,
          templatesResponse,
          publishedResponse,
          subscriptionsResponse,
        ] = await Promise.all([
          http.get(api.store_report_templates(this.selectedStoreId)),
          http.get(api.store_report_definitions(this.selectedStoreId)),
          http.get(api.store_public_report_templates(this.selectedStoreId)),
          http.get(api.store_published_report_definitions(this.selectedStoreId)),
          http.get(api.store_custom_report_subscriptions(this.selectedStoreId)),
        ]);
        this.systemTemplates =
          systemTemplatesResponse?.data?.data?.templates || [];
        this.savedReports =
          definitionsResponse?.data?.data?.definitions || [];
        this.publicTemplates =
          templatesResponse?.data?.data?.templates || [];
        this.publishedReports =
          publishedResponse?.data?.data?.definitions || [];
        this.customReportSubscriptions =
          subscriptionsResponse?.data?.data?.subscriptions || [];
      } catch (error) {
        this.systemTemplates = [];
        this.savedReports = [];
        this.publicTemplates = [];
        this.publishedReports = [];
        this.customReportSubscriptions = [];
        toast.error("The report library could not be loaded.");
      } finally {
        this.isLoadingReportLibrary = false;
      }
    },
    formatLibraryTime(value) {
      return value ? convertGMTToLocalTime(value) : "recently";
    },
    openSubscriptionDialog() {
      this.subscriptionForm = {
        definitionId: this.publishedReports[0]?.id || "",
        frequency: "DAILY",
        outputFormat: "XLSX",
      };
      this.showSubscriptionDialog = true;
    },
    async createCustomSubscription() {
      if (!this.subscriptionForm.definitionId || this.isSavingSubscription) {
        return;
      }
      this.isSavingSubscription = true;
      try {
        await http.post(
          api.store_custom_report_subscriptions(this.selectedStoreId),
          {
            definition_id: this.subscriptionForm.definitionId,
            frequency: this.subscriptionForm.frequency,
            output_format: this.subscriptionForm.outputFormat,
          },
        );
        this.showSubscriptionDialog = false;
        await this.fetchReportLibrary();
        toast.success("Custom report subscription created");
      } catch (error) {
        toast.error("The custom report subscription could not be created.");
      } finally {
        this.isSavingSubscription = false;
      }
    },
    async deleteCustomSubscription(subscription) {
      try {
        await http.delete(api.store_custom_report_subscription(
          this.selectedStoreId,
          subscription.uuid,
        ));
        await this.fetchReportLibrary();
        toast.success("Custom report subscription removed");
      } catch (error) {
        toast.error("The subscription could not be removed.");
      }
    },
    formatSubscriptionFrequency(frequency) {
      return String(frequency || "")
        .toLowerCase()
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
    },
    normalizeStoreId(value) {
      if (value == null) return null;
      if (typeof value === "object") {
        return value.id || null;
      }
      const parsed = Number(value);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    },
    normalizeRemoteReportConfig(reportTypes = []) {
      return reportTypes.reduce((config, report) => {
        if (!report?.type) return config;
        config[report.type] = {
          label: report.label,
          description: report.description,
          requiresDateRange: Boolean(report.requires_date_range),
          formats: report.allowed_formats || ["XLSX"],
          defaultFormat: report.default_format || null,
          previewEnabled: report.preview_enabled !== false,
          sortOrder: report.sort_order || 999,
          shortLabel: report.short_label || report.label,
          dashboardRedirectPath: report.dashboard_redirect_path || null,
          mobileEnabled: report.mobile_enabled !== false,
          category: report.category || null,
        };
        return config;
      }, {});
    },
    syncSelectedStoreFromRoute() {
      const routeStoreId = this.normalizeStoreId(this.$route.params.storeId);
      if (!routeStoreId) return;
      const currentStoreId = this.normalizeStoreId(
        this.$store.state.dashboard?.selectedStore,
      );
      if (String(routeStoreId) !== String(currentStoreId)) {
        this.$store.commit("SET_SELECTED_STORE", routeStoreId);
      }
    },
    applyRouteReportTypePreset() {
      const requestedType = String(this.$route.query.reportType || "").trim();
      if (!requestedType || !this.reportTypeConfig[requestedType]) {
        return;
      }
      if (this.reportRequest.type !== requestedType) {
        this.reportRequest.type = requestedType;
        return;
      }
      this.syncReportFormatWithType(requestedType);
    },
    async fetchReportCatalog() {
      if (!this.selectedStoreId) {
        this.remoteReportTypeConfig = {};
        return;
      }

      this.isLoadingReportCatalog = true;
      try {
        const response = await http.get(
          api.store_reports(this.selectedStoreId),
        );
        const reportTypes = response?.data?.data?.report_types || [];
        this.remoteReportTypeConfig =
          this.normalizeRemoteReportConfig(reportTypes);
      } catch (error) {
        this.remoteReportTypeConfig = {};
        console.error("Failed to fetch store report catalog:", error);
      } finally {
        this.isLoadingReportCatalog = false;
        if (!this.reportTypeConfig[this.reportRequest.type]) {
          this.resetReportForm();
        }
        this.applyRouteReportTypePreset();
        this.syncReportFormatWithType(this.reportRequest.type);
      }
    },
    getTodayDateString() {
        // Get today's date in local timezone for date filters.
      // Date inputs interpret date strings as UTC, so we convert local time to "fake UTC" ISO date
      const now = new Date();
      const offset = now.getTimezoneOffset();
      const local = new Date(now.getTime() - offset * 60 * 1000);
      return local.toISOString().split("T")[0];
    },
    normalizeDateInputValue(dateValue) {
      // Normalize date input value to ensure it's in YYYY-MM-DD format
      // HTML date inputs can sometimes have timezone issues, so we normalize
      if (!dateValue) return "";
      const str = String(dateValue).trim();
      // Extract just the date part (YYYY-MM-DD) if it includes time
      const datePart = str.split("T")[0].split(" ")[0];
      // Validate format
      if (/^\d{4}-\d{2}-\d{2}$/.test(datePart)) {
        return datePart;
      }
      return str;
    },
    syncReportFormatWithType(typeKey) {
      const config = this.reportTypeConfig[typeKey];
      if (!config) {
        this.reportRequest.format = "XLSX";
        return;
      }
      if (
        config.defaultFormat &&
        config.formats.includes(config.defaultFormat)
      ) {
        this.reportRequest.format = config.defaultFormat;
      }
      if (!config.formats.includes(this.reportRequest.format)) {
        this.reportRequest.format = config.formats[0];
      }
      if (config.requiresDateRange) {
        // Set to today if date range is required (UTC date for date input)
        // Use the same date string for both to ensure they're identical
        const today = this.getTodayDateString();
        this.reportRequest.startDate = today;
        this.reportRequest.endDate = today;
      } else {
        // Clear dates if date range is not required
        this.reportRequest.startDate = "";
        this.reportRequest.endDate = "";
      }
    },
    resetReportForm() {
      const requestedType = String(this.$route.query.reportType || "").trim();
      const currentType =
        requestedType && this.reportTypeConfig[requestedType]
          ? requestedType
          : this.reportRequest.type || this.reportTypeOptions[0]?.value || "";
      const config = this.reportTypeConfig[currentType];
      const defaultFormat =
        (config && config.defaultFormat) ||
        (config && config.formats && config.formats[0]) ||
        "XLSX";
      // Get today's date in local timezone using helper method
      // Use the same date string for both to ensure they're identical
      const today = this.getTodayDateString();
      this.reportRequest = {
        type: currentType,
        format: defaultFormat,
        startDate: config?.requiresDateRange ? today : "",
        endDate: config?.requiresDateRange ? today : "",
      };
    },
    getReportFormatLabel(formatKey) {
      return this.reportFormatLabels[formatKey] || formatKey;
    },
    buildDateTimeISO(dateValue, boundary = "start") {
      if (!dateValue) {
        return null;
      }
      // Ensure we have a clean date string (YYYY-MM-DD format)
      // Remove any time or timezone information that might be present
      const dateStrOnly = String(dateValue).split("T")[0].split(" ")[0];
      if (!dateStrOnly || !/^\d{4}-\d{2}-\d{2}$/.test(dateStrOnly)) {
        console.error("Invalid date format:", dateValue);
        return null;
      }

      // Parse the date string (YYYY-MM-DD format from date input)
      const [year, month, day] = dateStrOnly.split("-").map(Number);

      // Validate parsed values
      if (isNaN(year) || isNaN(month) || isNaN(day)) {
        console.error("Invalid date values:", { year, month, day });
        return null;
      }

      // Create date in local timezone (not UTC)
      const localDate = new Date(year, month - 1, day);

      // Verify the date was created correctly (check for timezone issues)
      if (
        localDate.getFullYear() !== year ||
        localDate.getMonth() !== month - 1 ||
        localDate.getDate() !== day
      ) {
        console.error("Date creation mismatch:", {
          input: { year, month, day },
          created: {
            year: localDate.getFullYear(),
            month: localDate.getMonth() + 1,
            day: localDate.getDate(),
          },
        });
      }

      if (boundary === "end") {
        // Set to end of day in local timezone: 23:59:59.999
        localDate.setHours(23, 59, 59, 999);
      } else {
        // Set to start of day in local timezone: 00:00:00.000
        localDate.setHours(0, 0, 0, 0);
      }

      // Format as local time ISO string without timezone
      // Use local time methods to ensure we get local time, not UTC
      const formattedDateStr =
        localDate.getFullYear() +
        "-" +
        (localDate.getMonth() + 1).toString().padStart(2, "0") +
        "-" +
        localDate.getDate().toString().padStart(2, "0");
      const hours = localDate.getHours().toString().padStart(2, "0");
      const minutes = localDate.getMinutes().toString().padStart(2, "0");
      const seconds = localDate.getSeconds().toString().padStart(2, "0");
      const milliseconds = localDate
        .getMilliseconds()
        .toString()
        .padStart(3, "0");

      // Return local time string WITHOUT timezone indicator (no Z, no offset)
      const result = `${formattedDateStr}T${hours}:${minutes}:${seconds}.${milliseconds}`;
      return result;
    },
    async handleGenerateReport() {
      if (!this.canSubmitReportRequest || !this.selectedStoreId) {
        toast.error("Select a store and fill in the report details first.");
        return;
      }

      const config = this.currentReportConfig;
      const payload = {
        report_type: this.reportRequest.type,
        report_format: this.reportRequest.format,
      };

      if (config?.requiresDateRange) {
        // Normalize and ensure we're using the exact date string from the input (YYYY-MM-DD format)
        const startDateStr = this.normalizeDateInputValue(
          this.reportRequest.startDate,
        );
        const endDateStr = this.normalizeDateInputValue(
          this.reportRequest.endDate,
        );

        const startDateResult = this.buildDateTimeISO(startDateStr, "start");
        const endDateResult = this.buildDateTimeISO(endDateStr, "end");

        // Explicitly ensure these are strings and not Date objects
        // This prevents any automatic serialization that might add "Z" suffix
        payload.start_date = startDateResult ? String(startDateResult) : null;
        payload.end_date = endDateResult ? String(endDateResult) : null;
      }

      this.isSubmittingReport = true;
      try {
        const { data } = await http.post(
          api.store_reports(this.selectedStoreId),
          payload,
        );
        const taskId = data?.data?.task_id;
        const taskRecordId = data?.data?.celery_task_record_id;
        if (taskId) {
          const taskData = {
            reportLabel: config?.label || "Store Report",
            reportType: this.reportRequest.type,
            reportFormat: this.reportRequest.format,
            taskRecordId,
          };

          // Store date range if available (for reports that require it)
          if (
            config?.requiresDateRange &&
            this.reportRequest.startDate &&
            this.reportRequest.endDate
          ) {
            taskData.startDate = this.reportRequest.startDate;
            taskData.endDate = this.reportRequest.endDate;
          }

          this.addBackgroundTask(
            taskId,
            "store_report",
            taskData,
            this.selectedStoreId,
          );
          this.selectedPreviewTaskId = taskId;
          toast.success(
            config?.previewEnabled === false
              ? "Report is being prepared"
              : "Preview is being prepared",
          );
        } else {
          toast.error("Failed to start report generation");
        }
      } catch (error) {
        const message =
          formatErrorMessage(error) || "Could not queue report generation";
        toast.error(message);
        console.error("Report generation error:", error);
      } finally {
        this.isSubmittingReport = false;
      }
    },
    normalizeTaskDateForDownload(value) {
      if (!value) return "";
      const normalized = String(value).trim();
      const directDate = normalized.split("T")[0].split(" ")[0];
      if (/^\d{4}-\d{2}-\d{2}$/.test(directDate)) {
        return directDate;
      }

      const parsed = new Date(normalized);
      if (Number.isNaN(parsed.getTime())) {
        return "";
      }
      const year = parsed.getFullYear();
      const month = String(parsed.getMonth() + 1).padStart(2, "0");
      const day = String(parsed.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    expandRequestedFormats(task) {
      const rawFormats = [
        ...(Array.isArray(task?.data?.result?.requested_formats)
          ? task.data.result.requested_formats
          : []),
      ];

      if (!rawFormats.length && task?.data?.reportFormat) {
        rawFormats.push(task.data.reportFormat);
      }

      const config = this.reportTypeConfig[task?.data?.reportType] || null;
      const expanded = [];
      rawFormats.forEach((formatKey) => {
        const normalized = String(formatKey || "").toUpperCase();
        if (!normalized) return;
        if (normalized === "BOTH") {
          (config?.formats || ["PDF", "XLSX"]).forEach((allowedFormat) => {
            const normalizedAllowed = String(allowedFormat || "").toUpperCase();
            if (normalizedAllowed && normalizedAllowed !== "BOTH") {
              expanded.push(normalizedAllowed);
            }
          });
          return;
        }
        expanded.push(normalized);
      });

      if (!expanded.length && config?.defaultFormat) {
        return [config.defaultFormat];
      }

      return [...new Set(expanded)];
    },
    downloadOptionsForTask(task) {
      const files = task?.data?.result?.files;
      if (Array.isArray(files) && files.length) {
        return files;
      }

      if (this.isTaskRunning(task) || this.isTaskFailed(task)) {
        return [];
      }

      return this.expandRequestedFormats(task).map((format) => ({
        format,
        manual: true,
      }));
    },
    buildManualReportDownloadUrl(task, formatKey) {
      if (!this.selectedStoreId || !task?.data?.reportType || !formatKey) {
        return "";
      }

      const taskParameters = task?.data?.result?.parameters || {};
      const queryParams = {
        report_type: task.data.reportType,
        format: String(formatKey).toUpperCase(),
      };

      const startDate =
        task?.data?.startDate ||
        taskParameters.start_date ||
        taskParameters.startDate;
      const endDate =
        task?.data?.endDate ||
        taskParameters.end_date ||
        taskParameters.endDate;

      const normalizedStartDate = this.normalizeTaskDateForDownload(startDate);
      const normalizedEndDate = this.normalizeTaskDateForDownload(endDate);

      if (normalizedStartDate) {
        queryParams.start_date = normalizedStartDate;
      }
      if (normalizedEndDate) {
        queryParams.end_date = normalizedEndDate;
      }

      const base = (process.env.VUE_APP_BACKEND_API || "").replace(/\/$/, "");
      return `${base}${api.store_report_download(
        this.selectedStoreId,
        queryParams,
      )}`;
    },
    downloadReportFile(task, file) {
      let fullUrl = "";
      if (file?.download_path) {
        const base = (process.env.VUE_APP_BACKEND_API || "").replace(/\/$/, "");
        fullUrl = `${base}${file.download_path}`;
      } else {
        fullUrl = this.buildManualReportDownloadUrl(task, file?.format);
      }

      if (!fullUrl) {
        toast.error("Download link unavailable");
        return;
      }

      window.open(fullUrl, "_blank", "noopener");
    },
    removeExportTask(taskId) {
      if (!taskId) return;
      this.removeBackgroundTask(taskId);
      if (String(this.selectedPreviewTaskId) === String(taskId)) {
        this.selectedPreviewTaskId = null;
      }
    },
    clearStoreExports() {
      if (!this.selectedStoreId) return;
      // Only clear report-related tasks, not all background tasks
      this.clearBackgroundTasksForStore(this.selectedStoreId, "store_report");
      this.selectedPreviewTaskId = null;
    },
    selectPreviewTask(taskId) {
      this.selectedPreviewTaskId = taskId;
    },
    isPreviewTaskSelected(task) {
      return String(task?.id) === String(this.activeReportTask?.id);
    },
    taskRequiresDateRange(task) {
      if (!task?.data?.reportType) return false;
      const config = this.reportTypeConfig[task.data.reportType];
      return config?.requiresDateRange || false;
    },
    formatDateRange(startDate, endDate) {
      if (!startDate || !endDate) return "";

      try {
        // Parse the date strings (YYYY-MM-DD format)
        const start = new Date(startDate + "T00:00:00");
        const end = new Date(endDate + "T00:00:00");

        // Check if dates are valid
        if (isNaN(start.getTime()) || isNaN(end.getTime())) return "";

        // Format dates
        const startFormatted = formatDate(start);
        const endFormatted = formatDate(end);

        // If same day, return single date
        if (startDate === endDate) {
          return startFormatted;
        }

        // Return date range
        return `${startFormatted} - ${endFormatted}`;
      } catch (error) {
        console.error("Error formatting date range:", error);
        return "";
      }
    },
    formatGenerationTime(startedAt) {
      if (!startedAt) return "";
      return convertGMTToLocalTime(startedAt);
    },
    isTaskFailed(task) {
      if (!task) return false;
      const status = task.status?.toUpperCase();
      return (
        status === "FAILURE" ||
        status === "FAILED" ||
        status === "ERROR" ||
        task.data?.error ||
        task.data?.result?.error
      );
    },
    taskStatusTone(task) {
      if (this.isTaskFailed(task)) return "danger";
      if (this.isTaskRunning(task)) return "info";
      const status = String(task?.status || "").toUpperCase();
      if (["SUCCESS", "COMPLETED", "DONE"].includes(status)) return "success";
      return "neutral";
    },
  },
  async mounted() {
    this.syncSelectedStoreFromRoute();
    await Promise.all([
      this.fetchReportCatalog(),
      this.fetchReportLibrary(),
    ]);
  },
};
</script>

<style scoped>
.reports-page {
  display: grid;
  gap: 1rem;
}

.reports-grid {
  align-items: start;
  display: grid;
  gap: 1rem;
  grid-template-columns: minmax(0, 1fr) minmax(19rem, 24rem);
}

.reports-main {
  display: grid;
  gap: 1rem;
  min-width: 0;
}

.reports-card {
  background: color-mix(in srgb, var(--hs-surface, #fff) 94%, transparent);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 14px;
  padding: 1rem;
}

.reports-kicker {
  color: var(--hs-primary, #3293ec);
  font-size: 0.72rem;
  font-weight: 950;
  letter-spacing: 0.1em;
  margin: 0 0 0.35rem;
  text-transform: uppercase;
}

.report-studio {
  display: grid;
  gap: 1rem;
}

.report-library {
  display: grid;
  gap: 1.25rem;
}

.report-library__head,
.report-library__group-head,
.report-library__row,
.report-library__identity,
.report-library__actions {
  align-items: center;
  display: flex;
  gap: 0.75rem;
}

.report-library__head,
.report-library__group-head {
  justify-content: space-between;
}

.report-library__head {
  align-items: flex-start;
}

.report-library h2,
.report-library h3 {
  color: var(--hs-text, #0f172a);
  margin: 0;
}

.report-library h2 {
  font-size: 1.1rem;
}

.report-library h3 {
  font-size: 0.88rem;
  font-weight: 950;
}

.report-library__head p,
.report-library__empty {
  color: var(--hs-muted, #64748b);
  font-size: 0.82rem;
  font-weight: 700;
  line-height: 1.5;
  margin: 0.25rem 0 0;
}

.report-library__group {
  display: grid;
  gap: 0.65rem;
}

.report-library__rows {
  border-bottom: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-top: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
}

.report-library__row {
  min-height: 4.25rem;
  padding: 0.75rem 0;
}

.report-library__row + .report-library__row {
  border-top: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
}

.report-library__identity {
  flex: 1 1 auto;
  min-width: 0;
}

.report-library__identity > div {
  display: grid;
  min-width: 0;
}

.report-library__identity strong {
  color: var(--hs-text, #0f172a);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.report-library__identity span {
  color: var(--hs-muted, #64748b);
  font-size: 0.74rem;
}

.report-library__actions {
  flex: 0 0 auto;
  gap: 0.35rem;
}

.report-library__form {
  display: grid;
  gap: 1rem;
}

.report-studio__head,
.reports-recent__head {
  align-items: flex-start;
  display: flex;
  gap: 1rem;
  justify-content: space-between;
}

.report-studio__actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  justify-content: flex-end;
}

.report-studio h2 {
  color: var(--hs-text, #0f172a);
  font-size: 1.25rem;
  font-weight: 950;
  margin: 0;
}

.report-studio p,
.reports-recent p,
.reports-task p,
.reports-status p {
  color: var(--hs-muted, #64748b);
  font-size: 0.84rem;
  font-weight: 700;
  line-height: 1.55;
  margin: 0.3rem 0 0;
}

.report-studio__fields,
.report-studio__dates {
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.report-studio__dates {
  border-top: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  padding-top: 1rem;
}

.report-studio__description {
  max-width: 52rem;
}

.reports-status {
  align-items: center;
  background: color-mix(in srgb, #22c55e 8%, var(--hs-surface, #fff));
  border: 1px solid color-mix(in srgb, #22c55e 22%, var(--hs-border, rgba(148, 163, 184, 0.22)));
  border-radius: 14px;
  display: flex;
  gap: 0.85rem;
  padding: 1rem;
}

.reports-status strong,
.reports-task strong {
  color: var(--hs-text, #0f172a);
  font-size: 0.9rem;
  font-weight: 900;
}

.reports-sidebar {
  min-width: 0;
  position: sticky;
  top: 1rem;
}

.reports-recent {
  display: grid;
  gap: 1rem;
}

.reports-recent__actions {
  align-items: center;
  display: inline-flex;
  gap: 0.4rem;
}

.reports-task-list {
  display: grid;
  gap: 0.75rem;
}

.reports-task {
  background: color-mix(in srgb, var(--hs-bg-soft, #f8fafc) 62%, transparent);
  border: 1px solid var(--hs-border, rgba(148, 163, 184, 0.22));
  border-radius: 14px;
  display: grid;
  gap: 0.75rem;
  padding: 0.9rem;
}

.reports-task.is-selected {
  border-color: color-mix(in srgb, #22c55e 42%, var(--hs-border, rgba(148, 163, 184, 0.22)));
  box-shadow: 0 0 0 3px color-mix(in srgb, #22c55e 10%, transparent);
}

.reports-task__head,
.reports-task__buttons {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  justify-content: space-between;
}

.reports-task__buttons {
  justify-content: flex-start;
}

.reports-task__message {
  align-items: center;
  color: #15803d;
  display: inline-flex;
  font-size: 0.78rem;
  font-weight: 850;
  gap: 0.35rem;
}

.reports-task__message--danger {
  align-items: flex-start;
  color: #dc2626;
}

.reports-task__remove {
  justify-self: end;
}

@media (max-width: 980px) {
  .reports-grid {
    grid-template-columns: 1fr;
  }

  .reports-sidebar {
    position: static;
  }
}

@media (max-width: 720px) {
  .report-studio__head,
  .reports-recent__head {
    align-items: stretch;
    flex-direction: column;
  }

  .report-studio__fields,
  .report-studio__dates {
    grid-template-columns: 1fr;
  }

  .report-library__head,
  .report-library__row {
    align-items: stretch;
    flex-direction: column;
  }

  .report-library__actions {
    justify-content: flex-start;
  }
}
</style>
